import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Row, Col, Table } from 'react-bootstrap';
import axios from '../axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../Functions/manage_orders.css';
import NavigationBar from '../NavigationBar';
import AddNewCommercialForm from '../Forms/add_new_commercial_form';
import {renderPagination, renderAdvancedPagination} from '../utilities';
import { formatNumberWithCommas } from '../utilities';
import { useCallback } from 'react';
import debounce from 'lodash/debounce';
import EditDisplayModal from './EditDisplayModal';
import CommercialPdfSignatureStamper from '../PDFs/ComponentsOfCommercialPDF/CommercialPdfSignatureStamper';
import { useCommLogSummary, CommLogButton, CommLogStatus, CommunicationLogDrawer } from "../CommunicationLog/CommunicationLog";


function Commercial() {
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [team_options, set_team_options]=useState([]);
    const [party_bank_options, set_party_bank_options]=useState([]);
    const [party_options, set_party_options]=useState([]);
    const [filters, setFilters] = useState({
        partyname: '',
        party_bank_id: ''
    });
    const [orders, setOrders] = useState([]);
    const [show_display_modal, set_show_display_modal] = useState(false);
    const [display_modal_commercial_id, set_display_modal_commercial_id] = useState(null);
    const [commLogSummary, refreshCommLogSummary] = useCommLogSummary('COMMERCIAL', orders.map(o => o.CommercialId));
    const [commLogTarget, setCommLogTarget] = useState(null);
    const openCommLog = (commercial) => setCommLogTarget({ type: 'COMMERCIAL', id: commercial.CommercialId, title: `Com #${commercial.CommercialId} (${commercial.InvoiceNo})`, subtitle: `${commercial.PartyName} · LC ${commercial.LcNo || '-'}` });

    useEffect(() => {
        fetch_dropdown_options();
    }, []); 

    const fetch_dropdown_options = async () => {
        try {
            const response2 = await axios.get(`${window.backendUrl}/get_all_parties`);
            const response3 = await axios.get(`${window.backendUrl}/get_all_party_banks_with_address`);

            const options2 = response2.data.data.map((party) => party['PartyName']);
            const options3 = response3.data.data.map((party_bank) => {
                const name = party_bank['Name'] || '';
                const address = party_bank['Address'] || '';
                
                const shortName = name.split(' ').slice(0, 2).join(' ');
                
                let shortAddress = address;
                if (address.length > 25) {
                    const truncated = address.substring(0, 25);
                    const lastSpaceIndex = truncated.lastIndexOf(' ');
                    shortAddress = lastSpaceIndex > 0 ? truncated.substring(0, lastSpaceIndex) + '...' : truncated + '...';
                }

                return {
                    value: party_bank['PartyBankID'], 
                    label: `${shortName} - ${shortAddress}`
                };
            });

            set_party_options(options2);
            set_party_bank_options(options3);
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };
    
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFilters((prevFormData) => ({ ...prevFormData, [name]: value }));
        setCurrentPage(1);
    };

    const fetchDataWithFilters = (page, filters) => {
        const {partyname, party_bank_id} = filters; 
        
        axios.get(`${window.backendUrl}/get_all_commercials`, {
            params: {partyname, party_bank_id, page}
        })
        .then((response) => {
            setOrders(response.data.data);
            setCurrentPage(response.data.currentPage);
            setTotalPages(response.data.totalPages);
        })
        .catch((error) => {
            console.log(error);
        });
    };

    const debouncedFetchData = useCallback(
        debounce((page, filters) => {
            fetchDataWithFilters(page, filters);
        }, 300),
        []
    );
    
    useEffect(() => {
        debouncedFetchData(currentPage, filters);
        return () => {
            debouncedFetchData.cancel();
        };
    }, [filters, currentPage, debouncedFetchData]);

    // Letters and PDFs open in their own tab; reload when the user comes back so a
    // letter saved there shows as saved (green bank icon) without a manual refresh.
    useEffect(() => {
        const onFocus = () => debouncedFetchData(currentPage, filters);
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [filters, currentPage, debouncedFetchData]);

    const [selected_commercial_id, set_selected_commercial_id] = useState(null);

    const handle_edit_click = (commercial_id) => {
        set_selected_commercial_id(commercial_id);
    }

    const handle_display_click = (commercial_id) => {
        set_display_modal_commercial_id(commercial_id);
        set_show_display_modal(true);
    };

    const handle_delete_click = (commercial_id) => {
        if (!window.confirm('Are you sure you want to delete this commercial? This cannot be undone.')) {
            return;
        }

        axios
            .delete(`${window.backendUrl}/delete_commercial/${commercial_id}`)
            .then(() => {
                fetchDataWithFilters(currentPage, filters);
            })
            .catch((error) => {
                console.error('Error deleting commercial:', error);
                alert(error.response?.data?.error || 'Failed to delete commercial');
            });
    };

    return (
    <>
        <NavigationBar />
        <Container fluid>
            <Row  style={{margin:'20px 0px'}}>
                <Col xs='auto' style={{margin:'0px 20px'}}>
                    <h4 className='mb-3'>Commercial</h4>
                    <AddNewCommercialForm commercial_id={selected_commercial_id}/>
                </Col>
                <Col style={{marginTop:'50px'}}>
                    <Container style={{ backgroundColor: 'rgba(0,0,0,0.08)', padding: '10px 25px', borderRadius: '5px' }}>
                        <Row>
                            <Col>
                                <h5>Commercial Information</h5>
                                <CommercialPdfSignatureStamper />
                            </Col>
                            <Col>
                                {renderPagination({ totalPages, currentPage, setCurrentPage })}
                                {renderAdvancedPagination({ totalPages, currentPage, setCurrentPage })}
                            </Col>
                        </Row>
                        <Row className='mt-2 mb-2'>
                            <Form className='d-flex'>
                                <Form.Select style={{width:'250px', marginRight:'25px'}} name="partyname" value={filters.partyname} onChange={handleInputChange}>
                                    <option value="">All Parties</option>
                                    {party_options.map((party, index) => (
                                        <option key={index} value={party}>
                                        {party}
                                        </option>
                                    ))}
                                </Form.Select>
                                <Form.Select style={{width:'250px'}} name="party_bank_id" value={filters.party_bank_id} onChange={handleInputChange}>
                                    <option value="">All Party Banks</option>
                                    {party_bank_options.map((party_bank, index) => (
                                        <option key={index} value={party_bank.value}>
                                        {party_bank.label}
                                        </option>
                                    ))}
                                </Form.Select>
                                
                            </Form>
                        </Row>
                        <Row>
                            <Table bordered striped condensed="true" style={{ fontSize: '13px', overflowY: 'auto', backgroundColor:'white'  }}>
                                <thead className="cf" style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                                    <tr>
                                        <th>ID#</th>
                                        <th>Invoice No. / Party <br/> Name</th>
                                        <th>PI Info.</th>
                                        <th>LC No. / Date</th>
                                        <th>Export LC/SC <br/>No. / Date</th>
                                        <th>Net / Gross <br/>Weight</th>
                                        <th>Duration / <br/>Acceptance Terms</th>
                                        <th>LC Value</th>
                                        <th>Latest Update</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {orders.map((order, index) => (
                                        <tr key={index}>
                                        <td>{order.CommercialId}<hr style={{border: '1px solid black' }}/>{order.ComType != 'General' ? order.ComType : ''}</td>
                                        <td>{order.InvoiceNo}<hr style={{border: '1px solid black' }}/>{order.PartyName}</td>
                                        <td style={{width:'220px'}}>{order.PIs.map(pi => pi.PiNo).join(', ')}</td>
                                        <td>{order.LcNo}{(order.AmendmentInfo || '').trim() && (<div style={{ fontSize: '11px', color: '#6c757d' }} title="L/C amendment">Amend: {order.AmendmentInfo.trim()}{Number(order.AmendmentDate) > 0 && ` · ${new Date(order.AmendmentDate * 1000).toLocaleString('en-GB', { timeZone: 'Asia/Dhaka', day: '2-digit', month: '2-digit', year: 'numeric'}).replace(/\//g, '.')}`}</div>)}<hr style={{border: '1px solid black' }}/>{order.LcDate && (new Date(order.LcDate * 1000).toLocaleString('en-GB', { timeZone: 'Asia/Dhaka', day: '2-digit', month: '2-digit', year: 'numeric'}).replace(/\//g, '.'))}</td>
                                        <td>{order.ContractNo}<hr style={{border: '1px solid black' }}/>{order.ContractDate && (new Date(order.ContractDate * 1000).toLocaleString('en-GB', { timeZone: 'Asia/Dhaka', day: '2-digit', month: '2-digit', year: 'numeric'}).replace(/\//g, '.'))}</td>
                                        <td>{order.Netweight}<hr style={{border: '1px solid black' }}/>{order.Grossweight}</td>
                                        <td>{order.Duration} Days<hr style={{border: '1px solid black' }}/>{order.AcceptanceTerms}</td>
                                        <td>${formatNumberWithCommas(order.TotalPrice.toFixed(2))}</td>
                                        <td><CommLogStatus info={commLogSummary[order.CommercialId]} onClick={() => openCommLog(order)} /></td>
                                        <td>
                                            <i
                                                style={{ fontSize: '15px', cursor: 'pointer' }}
                                                className="print-button fa fa-print"
                                                title="View Original PDF"
                                                onClick={() => window.open(`/commercial-pdf-viewer/${order.CommercialId}`, '_blank')}
                                            ></i>

                                            <i
                                                style={{ fontSize: '15px', marginLeft: '8px', cursor: 'pointer' }}
                                                className="fa fa-table"
                                                title="Edit Commercial Display Content"
                                                onClick={() => handle_display_click(order.CommercialId)}
                                            ></i>

                                            <i
                                                style={{ fontSize: '15px', marginLeft: '8px', cursor: 'pointer' }}
                                                className="fa fa-edit"
                                                id="commercial_edit_icon"
                                                title="Edit Commercial Details"
                                                onClick={() => handle_edit_click(order.CommercialId)}
                                            ></i>

                                            {order.HasSavedDocument === 1 && (
                                                <i
                                                style={{ fontSize: '15px', marginLeft: '8px', cursor: 'pointer' }}
                                                className="fa fa-file-text-o"
                                                title="View Saved PDF"
                                                onClick={() => window.open(`/commercial-saved-pdf-viewer/${order.CommercialId}`, '_blank')}
                                                ></i>
                                            )}

                                            <i
                                                style={{
                                                    fontSize: '15px', marginLeft: '8px', cursor: 'pointer',
                                                    color: order.HasForwardingLetter === 1 ? '#198754' : undefined
                                                }}
                                                className="fa fa-university"
                                                title={order.HasForwardingLetter === 1
                                                    ? `Bank Forwarding Letter — saved (${order.ForwardingLetterRefNo})`
                                                    : 'Bank Forwarding Letter'}
                                                onClick={() => window.open(`/forwarding-letter/${order.CommercialId}`, '_blank')}
                                            ></i>

                                            <span style={{ marginLeft: '8px' }}>
                                                <CommLogButton info={commLogSummary[order.CommercialId]} onClick={() => openCommLog(order)} />
                                            </span>

                                            <i
                                                style={{ fontSize: '15px', marginLeft: '8px', cursor: 'pointer', color: '#dc3545' }}
                                                className="fa fa-trash"
                                                title="Delete Commercial"
                                                onClick={() => handle_delete_click(order.CommercialId)}
                                            ></i>
                                        </td>
                                        </tr>
                                    ))}
                                </tbody>

                            </Table>
                        </Row>
                    </Container>
                </Col>
            </Row>
        </Container>
        <EditDisplayModal
            show={show_display_modal}
            onHide={() => set_show_display_modal(false)}
            commercialId={display_modal_commercial_id}
        />
        <CommunicationLogDrawer target={commLogTarget} onHide={() => setCommLogTarget(null)} onSaved={refreshCommLogSummary} />
    </>
    )
}

export default Commercial
